import React, { useContext, useEffect, useMemo, useState } from "react";

// Services
import AuthenticationService from "../../services/AuthenticationService";
import CountryContext from "../../CountryContext";
import { useIntl } from "react-intl";

// UI
import SearchSelect from "../../ui-components/SearchSelect";
import Placeholder from "../../ui-components/Placeholder";
import { PageHeading } from "../../ui-components/Container";
import RadioGroup from "../../ui-components/RadioGroup";
import DatePicker from "../../ui-components/DatePicker";
import Select from "../../ui-components/Select";
import Button from "../../ui-components/Button";
import Tabs from "../../ui-components/Tabs";

import { MultipleVisibility } from "./MultipleVisibility";
import { VisibilityFilter } from "./VisibilityFilter";

import {
  getSectionIcon,
  useInternalUsers,
  getVisibilityLevel,
  getVisibilityOptions,
  getAnalysisPeriod,
  getUsersOptions, getPeriodOptions,
} from "./Analytics.lib";


import {
  AnalysisPeriod,
  CustomPeriod,
  PeriodType,
  VisibilityLevel,
  VisibilityType,
  WorksiteOwnershipType,
  VisibilityRoles
} from "./Analytics.type";
import { AnalyticsWorksites } from "./Worksites";
import { AnalyticsUsers } from "./Users";
import { AnalyticsProducts } from "./Products";
import { AnalyticsGame } from "./Game";
import { AnalyticsConfigurations } from "./Configurations";
import { AnalyticsResales } from "./Resales";
import { WorksiteType } from "./Worksites/Worksites.type";
import useCountryAxiosInstance from "../../hooks/useCountryAxiosInstance";
import {Visibility} from "./Visibility";
import CheckboxGroup from "../../ui-components/CheckboxGroup";

const today = new Date();
const start = today.setMonth(today.getMonth() - 3);


export function Analytics() {

  const intl = useIntl();
  const {country} = useContext(CountryContext);
  const userData = AuthenticationService.getUserData();
  const countryApiRequest = useCountryAxiosInstance();

  const roleType = userData.role_type;
  const visibilityLevel: VisibilityLevel = getVisibilityLevel(roleType);
  const permission = visibilityLevel === 1 ? roleType : `user_${userData.id}`;
  const internalUsers = useInternalUsers(country.code, permission);
  const [worksiteTypes, setWorksiteTypes] = useState<
    WorksiteType[] | undefined
  >();

  const [tabIndex, setTabIndex] = useState<number>(0);

  // Separate period states for past (tabs 0-3, 5) and future (tab 4) analytics
  const [pastPeriod, setPastPeriod] = useState<PeriodType>("last_month");
  const [pastCustomPeriod, setPastCustomPeriod] = useState<CustomPeriod>({
    start: new Date(start),
    end: new Date(),
  });

  const [futurePeriod, setFuturePeriod] = useState<PeriodType>("next_month");
  const _futureStart = new Date();
  const _futureEnd = new Date();
  _futureEnd.setMonth(_futureEnd.getMonth() + 3);
  const [futureCustomPeriod, setFutureCustomPeriod] = useState<CustomPeriod>({
    start: _futureStart,
    end: _futureEnd,
  });

  const [worksitesOwnershipFilter, setWorksitesOwnershipFilter] = useState<WorksiteOwnershipType[]>(["created"]);


  const pastAnalysisPeriod = useMemo<AnalysisPeriod>(
    () => getAnalysisPeriod(pastPeriod, pastCustomPeriod),
    [pastPeriod, pastCustomPeriod]
  );
  const futureAnalysisPeriod = useMemo<AnalysisPeriod>(
    () => getAnalysisPeriod(futurePeriod, futureCustomPeriod),
    [futurePeriod, futureCustomPeriod]
  );

  const [visibilityUserPerRole, setVisibilityUserPerRole] = useState<{
    [key in VisibilityRoles]?: { label: string; value: number };
  }>({});


  const visibilityUserIds = Object.entries(visibilityUserPerRole)
    .filter(([_, user]) => user)
    .map(([_, user]) => user.value);

  // Derived variables based on current tab
  const isResalesTab = tabIndex === 4;
  const period = isResalesTab ? futurePeriod : pastPeriod;
  const customPeriod = isResalesTab ? futureCustomPeriod : pastCustomPeriod;

  let tabNames = [
    "worksites",
    "configurations",
    "users",
    "products",
    "sales",
  ];
  if (visibilityLevel <= 3) {
    tabNames.push("game");
  }


  useEffect(() => {
    if (!worksiteTypes) {
      // @ts-ignore
      countryApiRequest
        .get(`/worksite_types/all`)
        .then((res) => {
          // @ts-ignore
          setWorksiteTypes(res.sort((a, b) => (a.order > b.order ? 1 : -1)));
        })
        // @ts-ignore
        .catch((err) => console.log(err));
    }
  }, []);

  const showCurrentTab = (tabIndex: number) => {
    switch (tabIndex) {
      case 0:
        return (
          <AnalyticsWorksites
            visibilityUserIds={visibilityUserIds}
            analysisPeriod={pastAnalysisPeriod}
            internalUsers={internalUsers ?? []}
            worksiteTypes={worksiteTypes}
            worksitesOwnershipFilter={worksitesOwnershipFilter}
          />
        );
      case 1:
        return <AnalyticsConfigurations
          visibilityUserIds={visibilityUserIds}
          analysisPeriod={pastAnalysisPeriod}
          worksitesOwnershipFilter={worksitesOwnershipFilter}
        />
      case 2:
        return (
          <AnalyticsUsers
            visibilityLevel={visibilityLevel}
            visibilityUserIds={visibilityUserIds}
            analysisPeriod={pastAnalysisPeriod}
          />
        );
      case 3:
        return (
          <AnalyticsProducts
            visibilityUserIds={visibilityUserIds}
            analysisPeriod={pastAnalysisPeriod}
            worksiteTypes={worksiteTypes}
            worksitesOwnershipFilter={worksitesOwnershipFilter}
          />
        );
      case 4:
        return (
          <AnalyticsResales
            visibilityLevel={visibilityLevel}
            visibilityUserIds={visibilityUserIds}
            analysisPeriod={futureAnalysisPeriod}
            worksitesOwnershipFilter={worksitesOwnershipFilter}
          />
        );
      case 5:
        return (
          internalUsers && <AnalyticsGame
            visibilityLevel={visibilityLevel}
            visibilityUserIds={visibilityUserIds}
            analysisPeriod={pastAnalysisPeriod}
            worksiteTypes={worksiteTypes}
            internalUsers={internalUsers}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-full pb-12 flex flex-col bg-white">
      <main className="flex-grow flex flex-col justify-center w-full mx-auto">
        <PageHeading
          title={intl.formatMessage({ id: "analytics" })}
          contentActions={
            <div>
              <div className="sm:hidden">
                <Tabs
                  setActiveTab={setTabIndex}
                  active={tabIndex}
                  tabs={tabNames.map((i) => ({
                    name: intl.formatMessage({ id: i }),
                  }))}
                />
              </div>
              <div className="hidden sm:flex">
                {tabNames.map((name, index) => {
                  const Icon = getSectionIcon(name);
                  return (
                    <div key={name} className="mr-4">
                      <Button
                        className="whitespace-nowrap"
                        styleType={tabIndex === index ? "default" : "empty"}
                        onClick={() => {
                          setTabIndex(index);
                        }}
                        full={undefined}
                      >
                        <div className="flex flex-row gap-2 items-center">
                          <div>
                            <Icon className="h-4 w-4" />
                          </div>
                          <span>{intl.formatMessage({ id: name })}</span>
                        </div>
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          }
        />
        {internalUsers && worksiteTypes ? (
          <div className="flex flex-col gap-1">
            <h1 className="text-gray-600 mt-2">
              {intl.formatMessage({
                id: "target_period",
                defaultMessage: "Periodo di riferimento",
              })}
            </h1>
            <div className="flex flex-col xl:flex-row w-full">
              <div>
                <RadioGroup
                  id="periodSelection"
                  title={undefined}
                  description={undefined}
                  horizontal
                  options={getPeriodOptions(tabIndex).map((i) => ({
                    value: i,
                    name: intl.formatMessage({ id: i }),
                  }))}
                  currentValue={period}
                  onChange={(value: PeriodType) => {
                    if (isResalesTab) {
                      setFuturePeriod(value);
                    } else {
                      setPastPeriod(value);
                    }
                  }}
                />
              </div>
              {period === "custom" ? (
                <div className="ml-12 flex flex-row gap-2 sm:gap-6 justify-center">
                  <div className="w-36">
                    <DatePicker
                      label="Inizio"
                      value={customPeriod.start}
                      onChange={(i: Date) => {
                        if (isResalesTab) {
                          setFutureCustomPeriod({ ...futureCustomPeriod, start: i });
                        } else {
                          setPastCustomPeriod({ ...pastCustomPeriod, start: i });
                        }
                      }}
                      minDate={
                        tabIndex === 4
                          ? null
                          : new Date((new Date().getFullYear() - 3).toString())
                      }
                      maxDate={tabIndex === 4 ? null : new Date()}
                    />
                  </div>
                  <div className="w-36">
                    <DatePicker
                      label="Fine"
                      value={customPeriod.end}
                      onChange={(i: Date) => {
                        if (isResalesTab) {
                          setFutureCustomPeriod({ ...futureCustomPeriod, end: i });
                        } else {
                          setPastCustomPeriod({ ...pastCustomPeriod, end: i });
                        }
                      }}
                      minDate={
                        tabIndex === 4
                          ? null
                          : new Date((new Date().getFullYear() - 3).toString())
                      }
                      maxDate={tabIndex === 4 ? null : new Date()}
                    />
                  </div>
                </div>
              ) : null}
            </div>
            {internalUsers && <VisibilityFilter
              visibilityUserPerRole={visibilityUserPerRole}
              internalUsers={internalUsers}
              setVisibilityUserPerRole={setVisibilityUserPerRole}
            />}
            <div>
              {/*exclude users and Game for ownershi filter, useless in Users, already present and works different in Game*/}
              {![2,5].includes(tabIndex) && (
                <div className="mt-2">
                  <CheckboxGroup
                    title={intl.formatMessage({ id: "worksites_ownership_filter" , defaultMessage: "Ownership"})}
                    description={""}
                    horizontal
                    options={['created', 'shared', 'visible'].map(i => ({value: i, name: intl.formatMessage({id: i})}))}
                    values={worksitesOwnershipFilter}
                    onChange={(selected: WorksiteOwnershipType) =>{
                      if (worksitesOwnershipFilter.includes(selected)) {
                        setWorksitesOwnershipFilter(worksitesOwnershipFilter.filter(i => i !== selected))
                      } else {
                        setWorksitesOwnershipFilter([...worksitesOwnershipFilter, selected])
                      }
                    }}
                  />
                </div>
              )}
            </div>
            {showCurrentTab(tabIndex)}
          </div>
        ) : (
          <div className="flex w-full justify-center mt-2">
            <div className="w-full flex flex-col gap-3">
              <Placeholder height="h-10" />
              <Placeholder height="h-10" />
              <div className="flex flex-col w-full mb-4 gap-y-5">
                <div className="flex flex-row gap-x-5">
                  <Placeholder width="w-1/2" height="h-60" />
                  <Placeholder width="w-1/2" height="h-60" />
                </div>
                <div className="flex flex-row gap-x-5">
                  <Placeholder width="w-1/2" height="h-60" />
                  <Placeholder width="w-1/2" height="h-60" />
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
